import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export const Lightbox = ({ images, index, onClose, onChange, name }) => {
  const touchX = useRef(null);
  const open = index !== null && index >= 0;
  const total = images.length;

  const step = (d) => onChange((index + d + total) % total);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      e.stopImmediatePropagation();
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prev;
    };
  });

  const onTouchStart = (e) => (touchX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex flex-col bg-ink/95 backdrop-blur-md"
          data-testid="lightbox"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="flex items-center justify-between px-5 py-4 text-sand">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em]" data-testid="lightbox-counter">
              {name} · {index + 1} / {total}
            </p>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              aria-label="Close"
              data-testid="lightbox-close"
              className="rounded-full border border-sand/30 p-2 transition-colors duration-200 hover:bg-sand hover:text-ink"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6 md:px-20">
            <button
              type="button"
              aria-label="Previous"
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full border border-sand/30 bg-ink/60 p-3 text-sand transition-colors duration-200 hover:bg-sand hover:text-ink md:left-8"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <AnimatePresence mode="wait">
              <motion.img
                key={images[index]}
                src={images[index]}
                alt={`${name} ${index + 1}`}
                data-testid="lightbox-image"
                onClick={(e) => e.stopPropagation()}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="max-h-[calc(100vh-6.5rem)] max-w-full select-none rounded-lg object-contain shadow-2xl"
                draggable={false}
              />
            </AnimatePresence>
            <button
              type="button"
              aria-label="Next"
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full border border-sand/30 bg-ink/60 p-3 text-sand transition-colors duration-200 hover:bg-sand hover:text-ink md:right-8"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
